/**
 * Dashboard — a rank-aware redirect, not a screen.
 * Route: /dashboard
 * @file src/web/features/main/pages/dashboard.tsx
 *
 * @llm-rule WHEN: Anything still links to /dashboard (the Bloom template default)
 * @llm-rule NOTE: Presynce has no single dashboard. A employee's day starts at
 *   the clock-in card; a lead's at their roster; a Head or CEO's at oversight.
 *   Kept as a redirect so old links and bookmarks land somewhere sensible.
 */

import { Navigate } from 'react-router-dom';
import { AuthGuard, useAuth } from '../../auth';
import { isSuperAdmin, isSupervisor, type OrgRole } from '../../../shared/domain';

function DashboardRedirect() {
  const { user, isLoading } = useAuth();

  if (isLoading) return null;
  if (!user) return <Navigate to="/auth/login" replace />;

  const role = (user.orgRole ?? 'EMPLOYEE') as OrgRole;

  // The platform operator has no company app to land in.
  if (isSuperAdmin(role)) return <Navigate to="/platform" replace />;
  if (role === 'COMPANY_ADMIN' || role === 'HEAD') return <Navigate to="/oversight" replace />;
  if (isSupervisor(role)) return <Navigate to="/team" replace />;
  return <Navigate to="/clock" replace />;
}

export default function DashboardRoute() {
  return (
    <AuthGuard>
      <DashboardRedirect />
    </AuthGuard>
  );
}
